import { IUserFact } from "@ms-ofb/officepersonalization/dist/lib/Common/Models";
import { TelemetryEvent } from "../Constants";
import * as Utils from "../Utils";
import { ComparatorType, CurrentTimeIntervalType, IUserFactInput, UserFactLatencyType, UserFactType } from "./UserFactModel";
import { TelemetryLogger } from "../TelemetryLogger";

const { isNOU, isNumber, isArray } = Utils;
const commonComparatorTypes: ComparatorType[] = [
	ComparatorType.Equal,
	ComparatorType.NotEqual,
	ComparatorType.GreaterThan,
	ComparatorType.GreaterThanOrEqual,
	ComparatorType.LessThan,
	ComparatorType.LessThanOrEqual,
];

export class UserFact {
	public static deserialize(rawUserFactSpec: any): UserFact {
		return this.validate(rawUserFactSpec) ? new UserFact(rawUserFactSpec) : null;
	}

	private static validate(input: any) {
		if (!input) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsSpecDeserialization.Failed,
				"Input is null or undefined for user fact");
			return false;
		}

		// Values is used for type UserFactType.ListOfStrings
		if (!input.Value && input.Values) {
			input.Value = input.Values;
		}

		if (isNOU(input.Name) || isNOU(input.Type) || isNOU(input.Value)) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsSpecDeserialization.Failed,
				`Either of the required parameters Name: ${input.Name}, Type: ${input.Type} or Value: ${input.Value} are not provided for the user fact`);
			return false;
		}

		if (!this.validateUserFactType(input.Type)) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsSpecDeserialization.Failed,
				`Invalid UserFactType value ${input.Type} is provided for user fact`);
			return false;
		}

		if (input.Type === UserFactType.ListFile) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsSpecDeserialization.Failed,
				`Unsupported UserFactType value ${input.Type} is provided for user fact`);
			return false;
		}

		if (input.LatencyType && !this.validateLatencyType(input.LatencyType)) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsSpecDeserialization.Failed,
				`Invalid LatencyType value ${input.LatencyType} is provided for user fact of type ${input.Type}`);
			return false;
		}

		if (input.LatencyType && input.LatencyType !== UserFactLatencyType.None && isNOU(input.LatencyDurationInSeconds)) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsSpecDeserialization.Failed,
				`LatencyDurationInSeconds value ${input.LatencyDurationInSeconds} is not provided for user fact of type ${input.Type}`);
			return false;
		}

		if (input.LatencyDurationInSeconds && !isNumber(input.LatencyDurationInSeconds)) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsSpecDeserialization.Failed,
				`Invalid LatencyDurationInSeconds value ${input.LatencyDurationInSeconds} is provided for user fact of type ${input.Type}`);
			return false;
		}

		if (input.Type === UserFactType.TimeIntervalInSeconds && isNOU(input.IntervalType)) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsSpecDeserialization.Failed,
				`Required parameter intervalType is not provided for user fact of type ${input.Type}`);
			return false;
		}

		const supportedComparatorTypes = this.getComparatorTypes(input.Type);
		if (input.Comparator && !isComparatorTypeSupported(input.Comparator, supportedComparatorTypes)) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsSpecIsAMatch.Failed,
				`Unsupported comparator type for user fact of type ${input.Type}`);
			return false;
		}

		return true;
	}

	private static getComparatorTypes(userFactType: UserFactType): ComparatorType[] {
		switch (userFactType) {
			case UserFactType.Boolean:
				return [ComparatorType.Equal, ComparatorType.NotEqual];
			case UserFactType.String:
				return [ComparatorType.Equal,
						ComparatorType.NotEqual,
						ComparatorType.In,
						ComparatorType.NotIn,
						ComparatorType.InverseIn,
						ComparatorType.InverseNotIn];
			case UserFactType.ListOfStrings:
				return [ComparatorType.In, ComparatorType.NotIn, ComparatorType.InverseIn, ComparatorType.InverseNotIn];
			case UserFactType.TimeIntervalInSeconds:
			case UserFactType.DateTimeUTC:
			case UserFactType.Number:
			case UserFactType.ListFile:
				return commonComparatorTypes;
			default:
				assertNever(userFactType);
		}

		return commonComparatorTypes;
	}

	private static validateLatencyType(latency: UserFactLatencyType) {
		return (
			latency === UserFactLatencyType.None ||
			latency === UserFactLatencyType.ClientIngestionDateTime ||
			latency === UserFactLatencyType.SourceDateTime ||
			latency === UserFactLatencyType.StorageDateTime
		) ? true : assertNever(latency);
	}

	private static validateUserFactType(factType: UserFactType) {
		return (
			factType === UserFactType.Boolean ||
			factType === UserFactType.DateTimeUTC ||
			factType === UserFactType.Number ||
			factType === UserFactType.String ||
			factType === UserFactType.TimeIntervalInSeconds ||
			factType === UserFactType.ListOfStrings ||
			factType === UserFactType.ListFile
		) ? true : assertNever(factType);
	}

	private input: IUserFactInput;

	constructor(input: IUserFactInput) {
		this.input = input;
	}

	public isAMatch(rawUserFact: IUserFact): boolean {
		if (!rawUserFact || !rawUserFact.userFactValue) {
			return false;
		}

		if (!this.isLatencyAcceptable(rawUserFact)) {
			return false;
		}

		try {
			const factType = this.getType();
			if (factType === UserFactType.Boolean) {
				return compareBooleanValues(this.getValue(), rawUserFact, this.getComparator());
			} else if (factType === UserFactType.DateTimeUTC) {
				return compareDateTimeUTCValues(this.getValue() as string, rawUserFact, this.getComparator());
			} else if (factType === UserFactType.Number) {
				return compareNumberValues(this.getValue() as number, rawUserFact, this.getComparator());
			} else if (factType === UserFactType.String) {
				return compareStringValues(this.getValue() as string, rawUserFact, this.getComparator());
			} else if (factType === UserFactType.TimeIntervalInSeconds) {
				return compareTimeIntervalValues(this.getValue() as number, rawUserFact, this.getComparator(), this.getIntervalType());
			} else if (factType === UserFactType.ListOfStrings) {
				return compareListValues(this.getValue() as unknown as string[], rawUserFact, this.getComparator());
			} else {
				TelemetryLogger.getTelemetryLogger().log_Error(
					TelemetryEvent.Floodgate.UserFactsSpecIsAMatch.Failed,
					`Unsupported type of user fact was provided ${factType}`);
				return false;
			}
		} catch (error) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsSpecIsAMatch.Failed,
				error);
			return false;
		}
	}

	public getType() {
		return this.input.Type;
	}

	public getName() {
		return this.input.Name;
	}

	public getValue() {
		return this.input.Value;
	}

	public getComparator() {
		return this.input.Comparator || ComparatorType.Equal;
	}

	public getLatencyType() {
		return this.input.LatencyType || UserFactLatencyType.None;
	}

	public getLatencyDurationInSeconds() {
		return this.input.LatencyDurationInSeconds;
	}

	public getIntervalType() {
		return this.input.IntervalType;
	}

	private isLatencyAcceptable(rawUserFact: IUserFact) {
		const factType = this.getType();

		if (!isNOU(this.getLatencyDurationInSeconds()) &&
			!evaluateFactCandidacy(this.getLatencyType(), this.getLatencyDurationInSeconds(), rawUserFact)) {
			TelemetryLogger.getTelemetryLogger().log_Event(
				TelemetryEvent.Floodgate.UserFactsSpecIsAMatch.Mismatch, {
					ErrorMessage: `Latency duration not acceptable - { Name: "${rawUserFact.userFactName}" }`,
					TimeMilliseconds: this.getLatencyDurationInSeconds(),
					Type: `${factType}`,
				},
			);
			return false;
		}

		return true;
	}
}

function evaluateFactCandidacy(latencyType: UserFactLatencyType, latencyDurationInSeconds: number, userFact: IUserFact) {
	let userFactTime: string;
	switch (latencyType) {
		case UserFactLatencyType.None:
			break;
		case UserFactLatencyType.SourceDateTime:
			userFactTime = userFact.sourceDateTime;
			break;
		case UserFactLatencyType.StorageDateTime:
			userFactTime = userFact.storageDateTime;
			break;
		case UserFactLatencyType.ClientIngestionDateTime:
			userFactTime = userFact.clientIngestionDateTime;
			break;
		default:
			assertNever(latencyType);
	}

	if (!isNOU(userFactTime)) {
		const userFactTimeInMilliseconds = Date.parse(userFactTime);
		const currentTimeInMilliseconds = Date.now();

		return currentTimeInMilliseconds - userFactTimeInMilliseconds <= (latencyDurationInSeconds * 1000);
	}

	return true;
}

function compareBooleanValues(userFactSpecValue: any, rawUserFact: IUserFact, comparator: ComparatorType) {
	const rawUserFactValueInBoolean = JSON.parse(rawUserFact.userFactValue.toLowerCase());
	return compareValues(!!userFactSpecValue, !!rawUserFactValueInBoolean, comparator);
}

function compareDateTimeUTCValues(userFactSpecValue: string, rawUserFact: IUserFact, comparator: ComparatorType) {
	const rawUserFactValueInMilliseconds = Date.parse(rawUserFact.userFactValue);
	if (!isAValidNumber(rawUserFactValueInMilliseconds, rawUserFact.userFactValue, rawUserFact.userFactName)) {
		return false;
	}
	const userFactSpecValueInMilliseconds = Date.parse(userFactSpecValue);
	if (!isAValidNumber(userFactSpecValueInMilliseconds, userFactSpecValue, null, UserFactType.DateTimeUTC)) {
		return false;
	}

	return compareValues(userFactSpecValueInMilliseconds, rawUserFactValueInMilliseconds, comparator);
}

function compareNumberValues(userFactSpecValue: number, rawUserFact: IUserFact, comparator: ComparatorType) {
	const rawUserFactValueInNumber = parseInt(rawUserFact.userFactValue, 10);
	if (!isAValidNumber(rawUserFactValueInNumber, rawUserFact.userFactValue, rawUserFact.userFactName)) {
		return false;
	}

	return compareValues(userFactSpecValue, rawUserFactValueInNumber, comparator);
}

function compareStringValues(userFactSpecValue: string, rawUserFact: IUserFact, comparator: ComparatorType) {
	return compareValues(userFactSpecValue.toLowerCase(), rawUserFact.userFactValue.toLowerCase(), comparator);
}

function compareTimeIntervalValues(
	userFactSpecValue: number, rawUserFact: IUserFact, comparator: ComparatorType, intervalType: CurrentTimeIntervalType) {
	const rawUserFactValueInMilliseconds = Date.parse(rawUserFact.userFactValue);
	if (!isAValidNumber(rawUserFactValueInMilliseconds, rawUserFact.userFactValue, rawUserFact.userFactName)) {
		return false;
	}

	// Diff from now and users input date
	// IntervalTo - Internval to current date from fact date (fact date is expected to be before current date)
	// IntervalFrom - Interval from current date to fact date (fact date is expected to be after current date)
	const currentTimeInMilliseconds = Date.now();
	const currentDateDiff = intervalType === CurrentTimeIntervalType.IntervalTo ?
		(currentTimeInMilliseconds - rawUserFactValueInMilliseconds) : (rawUserFactValueInMilliseconds - currentTimeInMilliseconds);
	if (currentDateDiff < 0) {
		TelemetryLogger.getTelemetryLogger().log_Event(
			TelemetryEvent.Floodgate.UserFactsSpecIsAMatch.Mismatch, {
			ErrorMessage: `Interval type not acceptable - { Spec: ${userFactSpecValue}, Name: "${rawUserFact.userFactName}" }`,
			TimeMilliseconds: currentDateDiff,
			Type: `${intervalType}`,
		},
		);

		return false;
	}

	const secondInMilliseconds = 1000;
	const userFactSpecValueInMilliseconds = userFactSpecValue * secondInMilliseconds;

	// check if the above diff satisfies the comparison criteria
	return compareValues(userFactSpecValueInMilliseconds, currentDateDiff, comparator);
}

function compareListValues<T extends string | boolean | number>(userFactSpecValue: T[], rawUserFact: IUserFact, comparator: ComparatorType) {
	return compareValues(userFactSpecValue, rawUserFact.userFactValue as T, comparator);
}

function isComparatorTypeSupported(comparator: ComparatorType, supportedComparatorTypes: ComparatorType[]) {
	return supportedComparatorTypes.indexOf(comparator) !== -1;
}

function compareValues<T extends string | boolean | number>(
	userFactSpecValue: T | T[], userFactRawValue: T, comparator: ComparatorType): boolean {
	switch (comparator) {
		case ComparatorType.Equal:
			return userFactRawValue === userFactSpecValue;
		case ComparatorType.NotEqual:
			return userFactRawValue !== userFactSpecValue;
		case ComparatorType.GreaterThan:
			return userFactRawValue > userFactSpecValue;
		case ComparatorType.GreaterThanOrEqual:
			return userFactRawValue >= userFactSpecValue;
		case ComparatorType.LessThan:
			return userFactRawValue < userFactSpecValue;
		case ComparatorType.LessThanOrEqual:
			return userFactRawValue <= userFactSpecValue;
		case ComparatorType.In:
			return isFactValueInTargetItem(userFactSpecValue, userFactRawValue);
		case ComparatorType.NotIn:
			return !isFactValueInTargetItem(userFactSpecValue, userFactRawValue);
		case ComparatorType.InverseIn:
			return isFactValueInTargetItem(userFactRawValue, userFactSpecValue as T);
		case ComparatorType.InverseNotIn:
			return !isFactValueInTargetItem(userFactRawValue, userFactSpecValue as T);
		default:
			assertNever(comparator);
	}
}

function assertNever(_: never) {
	return false;
}

function isFactValueInTargetItem<T>(userFactTargetValues: T | T[], userFactRawValue: T): boolean {
	if (typeof userFactRawValue === "string") {
		const factValue = userFactRawValue.toLowerCase();
		if (typeof userFactTargetValues === "string") {
			// both the inputs are strings, do a plain indexOf
			return (userFactTargetValues as string).toLowerCase().indexOf(factValue) !== -1;
		}

		if (isArray(userFactTargetValues)) {
			const specValues = userFactTargetValues as T[];
			return specValues.some((specValue) => specValue && specValue.toString().toLowerCase() === factValue);
		}
	}

	if (isArray(userFactTargetValues)) {
		return (userFactTargetValues as T[]).indexOf(userFactRawValue) !== -1;
	}

	return false;
}

function isAValidNumber(numberValue: number, value: string, rawUserFactName: string, factType?: UserFactType) {
	if (!isNumber(numberValue)) {
		let errorMessage = `User fact ${rawUserFactName} has invalid value ${value}`;
		if (factType) {
			errorMessage = `Invalid value ${value} was provided for user fact of type ${factType}`;
		}

		TelemetryLogger.getTelemetryLogger().log_Error(
			TelemetryEvent.Floodgate.UserFactsSpecIsAMatch.Failed,
			errorMessage);

		return false;
	}

	return true;
}
